import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { CreateCaseComponent } from './create-case/create-case.component';
import { ImpressumComponent } from './pages/impressum/impressum.component';
import { DatenschutzerklaerungComponent } from './pages/datenschutzerklaerung/datenschutzerklaerung.component';
import { NutzungsbedingungenComponent } from './pages/nutzungsbedingungen/nutzungsbedingungen.component';
import { WiderrufsbelehrungComponent } from './pages/widerrufsbelehrung/widerrufsbelehrung.component';

import { authGuard } from './auth/guards/auth.guard'; // Adjust path if necessary
import { roleGuard } from './auth/guards/role.guard';

export const routes: Routes = [
  {
    path: '', // Main view (Cases list) - Requires Login
    loadComponent: () => import('./cases/cases.component').then(c => c.CasesComponent),
    canActivate: [authGuard, roleGuard],
    data: { blockedRoles: ['student'] }
  },
  {
    path: 'account/change-password',
    loadComponent: () => import('@shared/components/change-password.component').then(m => m.ChangePasswordComponent),
    canActivate: [authGuard, roleGuard],
    data: { blockedRoles: ['student'] } // Graders allowed
  },
  {
    path: 'account/change-email',
    loadComponent: () => import('./shared/components/change-email.component').then(m => m.ChangeEmailComponent),
    canActivate: [authGuard, roleGuard],
    data: { blockedRoles: ['student'] } // Graders allowed
  },
  {
    path: 'account/settings',
    loadComponent: () => import('./shared/components/account-settings/account-settings.component').then(m => m.AccountSettingsComponent),
    canActivate: [authGuard, roleGuard],
    data: { blockedRoles: ['student'] } // Graders allowed
  },
  {
    path: 'billing/success',
    loadComponent: () => import('./shared/components/billing-return.component').then(m => m.BillingReturnComponent),
    canActivate: [authGuard, roleGuard],
    data: { blockedRoles: ['student'] }
  },
  {
    path: 'billing/cancel',
    loadComponent: () => import('./shared/components/billing-return.component').then(m => m.BillingReturnComponent),
    canActivate: [authGuard, roleGuard],
    data: { blockedRoles: ['student'] }
  },
  {
    path: 'billing/manage',
    loadComponent: () => import('./shared/components/billing-return.component').then(m => m.BillingReturnComponent),
    canActivate: [authGuard, roleGuard],
    data: { blockedRoles: ['student'] }
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then(m => m.AuthModule),
    // Note: No guard here - child routes can have their own guards
  },
  {
    path: 'case-editor',
    loadChildren: () => import('./case-editor/case-editor.module').then(m => m.CaseEditorModule),
    canActivate: [authGuard],
    // No role restriction — students access via course/pilot flow, graders via dashboard
  },
  {
    path: 'c/:id', // Viewing/Editing a Case - Requires Login AND 'admin' role
    component: CreateCaseComponent,
    canActivate: [authGuard, roleGuard],
    data: { blockedRoles: ['student', 'grader'] }
    //data: { roles: ['admin'] }
  },
  {
    path: 'create-case-editor',
    loadChildren: () => import('./create-case-editor/create-case-editor.module').then(m => m.CreateCaseEditorModule),
    canActivate: [authGuard, roleGuard],
    data: { roles: ['korrektor', 'admin', 'grader', 'kanzlei_admin', 'author'] }
  },
  {
    path: 'author/dashboard',
    loadComponent: () => import('./author/author-dashboard.component').then(m => m.AuthorDashboardComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: ['author', 'admin'] }
  },
  {
    path: 'author/cases',
    loadComponent: () => import('./author/author-cases.component').then(m => m.AuthorCasesComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: ['author', 'admin'] }
  },
  {
    path: 'author/earnings',
    loadComponent: () => import('./author/author-earnings.component').then(m => m.AuthorEarningsComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: ['author', 'admin'] }
  },
  {
    path: 'author/admin-moderation',
    loadComponent: () => import('./author/author-admin-moderation.component').then(m => m.AuthorAdminModerationComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: ['admin'] }
  },
  {
    path: 'case-created/:id',
    loadComponent: () => import('./shared/components/case-created-success.component').then(m => m.CaseCreatedSuccessComponent),
    canActivate: [authGuard, roleGuard],
    data: { blockedRoles: ['student'] }
  },
  {
    path: 'course/join/:token',
    loadComponent: () => import('./course/course-join.component').then(m => m.CourseJoinComponent),
    data: { hideLayout: true }
  },
  {
    path: 'org/join/:token',
    loadComponent: () => import('./org/org-join.component').then(m => m.OrgJoinComponent),
    data: { hideLayout: true }
  },
  {
    path: 'kanzlei/join/:code',
    loadComponent: () => import('./kanzlei/kanzlei-join.component').then(m => m.KanzleiJoinComponent),
    data: { hideLayout: true }
  },
  {
    path: 'kanzlei',
    loadComponent: () => import('./kanzlei/kanzlei-dashboard.component').then(m => m.KanzleiDashboardComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: ['kanzlei_admin', 'admin'] }
  },
  {
    path: 'go/einloesen',
    loadComponent: () => import('./auth/components/magic-login/magic-login.component').then(m => m.MagicLoginComponent),
    data: { hideLayout: true }
  },
  {
    path: 'go',
    loadComponent: () => import('./course/course-start.component').then(m => m.CourseStartComponent),
    data: { hideLayout: true }
  },
  {
    path: 'submitted',
    loadComponent: () => import('./course/submission-success.component').then(m => m.SubmissionSuccessComponent),
    data: { hideLayout: true }
  },
  {
    path: 'student/courses',
    loadComponent: () => import('./student/student-courses.component').then(m => m.StudentCoursesComponent),
    canActivate: [authGuard]
  },
  {
    path: 'student/course/:courseId',
    loadComponent: () => import('./student/student-course-detail.component').then(m => m.StudentCourseDetailComponent),
    canActivate: [authGuard]
  },
  {
    path: 'impressum',
    component: ImpressumComponent
  },
  {
    path: 'datenschutzerklaerung',
    component: DatenschutzerklaerungComponent
  },
  {
    path: 'nutzungsbedingungen',
    component: NutzungsbedingungenComponent
  },
  {
    path: 'widerrufsbelehrung',
    component: WiderrufsbelehrungComponent
  },
  // Benchmark Routes
  {
    path: 'benchmark/list',
    loadComponent: () => import('./benchmark/components/benchmark-list/benchmark-list.component').then(m => m.BenchmarkListComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: ['admin'] }
  },
  {
    path: 'benchmark/upload',
    loadComponent: () => import('./benchmark/components/benchmark-upload/benchmark-upload.component').then(m => m.BenchmarkUploadComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: ['admin'] }
  },
  {
    path: 'benchmark/annotation/:requestId',
    loadComponent: () => import('./benchmark/components/benchmark-annotation/benchmark-annotation.component').then(m => m.BenchmarkAnnotationComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: ['admin'] }
  },
  {
    path: 'benchmark/grading-eval',
    loadComponent: () => import('./benchmark/components/grading-eval/grading-eval.component').then(m => m.GradingEvalComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: ['admin'] }
  },

  {
    path: 'pilot',
    loadChildren: () => import('./pilot/pilot.module').then(m => m.PilotModule)
  },

  {
    // Interner visueller Prototyp für den LTO-Pilot (reiner Mock, kein Backend)
    path: 'lto-prototyp',
    loadComponent: () => import('./partner/lto-prototype/lto-prototype.component').then(m => m.LtoPrototypeComponent),
    data: { hideLayout: true }
  },

  {
    // B2C-Einstieg (Issue #384): konkreter Fall → Gast-Session → Editor, Account erst bei Abgabe
    path: 'start',
    loadComponent: () => import('./funnel/start.component').then(m => m.StartComponent),
    data: { hideLayout: true }
  },

  {
    path: '**', // Catch-all for unknown routes
    redirectTo: '' // Redirect to the main authenticated view (CasesComponent)
    // If the user is not logged in, the authGuard on '' will redirect them to /login
  }
];

@NgModule({
  imports: [RouterModule.forRoot(
    routes,
    {
      preloadingStrategy: PreloadAllModules,
      scrollPositionRestoration: 'disabled', // Disable automatic scroll restoration
      anchorScrolling: 'enabled',
      scrollOffset: [0, 0]
    }
  )],
  exports: [RouterModule]
})
export class AppRoutingModule { }
